import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatDate } from '@/lib/utils';
import { useFormatting } from '@/hooks/useFormatting';
import { getCashMovementTerms } from '@/lib/terminology-config';
import { Transaction } from '@/types';
import {
  Calendar,
  ArrowDownCircle,
  ArrowUpCircle,
  Tag,
  FileText,
  Save,
  X,
  Hash,
  Layers,
  Clock,
  Database,
  SquarePen,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { getAllTransactionCategories } from '@/api/services/transactionCategoryService';
import type { TransactionCategoryApi } from '@/api/models/transactionCategory';
import { useApp } from '@/contexts/AppContext';

// Extended transaction type that includes transactionType from API and display fields
type TransactionWithType = Transaction & {
  transactionType?: string;
  grossValueIn?: number;
  grossValueOut?: number;
  issueDate?: Date;
  billDate?: Date;
  dueDate?: Date;
};

interface TransactionDetailModalProps {
  open: boolean;
  onClose: () => void;
  transaction: TransactionWithType | null;
  onSave?: (transaction: TransactionWithType) => void;
}

export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  open,
  onClose,
  transaction,
  onSave,
}) => {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTransaction, setEditedTransaction] = useState<TransactionWithType | null>(transaction);
  const [categories, setCategories] = useState<TransactionCategoryApi[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    setEditedTransaction(transaction);
    setIsEditing(false);
  }, [transaction]);

  const DEMO_CATEGORIES = [
    'Customer Receipts', 'Other Income', 'Revenue',
    'Materials', 'Payroll', 'Subcontractors', 'Rent', 'Equipment',
    'Professional Services', 'Utilities', 'Insurance', 'Marketing',
    'Travel & Expenses', 'Bank Charges', 'Tax', 'Uncategorised',
  ];

  useEffect(() => {
    if (open) {
      if (mode === 'live') {
        setCategoriesLoading(true);
        getAllTransactionCategories()
          .then((response) => {
            setCategories(response.categories);
          })
          .catch((error) => {
            console.error('Failed to fetch categories:', error);
          })
          .finally(() => {
            setCategoriesLoading(false);
          });
      } else {
        // Demo mode — use flat list of all categories
        setCategories(
          DEMO_CATEGORIES.map((name, index) => ({
            id: index + 1,
            name,
            createdAt: '',
            updatedAt: '',
          }))
        );
      }
    }
  }, [open, mode]);

  if (!transaction || !editedTransaction) return null;

  const isIncome = (transaction.grossValueIn ?? 0) > 0;
  const displayAmount = isIncome
    ? (transaction.grossValueIn ?? Math.abs(transaction.amount))
    : (transaction.grossValueOut ?? Math.abs(transaction.amount));

  const handleSave = () => {
    if (onSave && editedTransaction) {
      onSave(editedTransaction);
      setIsEditing(false);
      onClose();
    }
  };

  const handleCancel = () => {
    setEditedTransaction(transaction);
    setIsEditing(false);
  };

  const canEdit = hasPermission('edit_transaction_categories');

  // Status badge variant
  const statusVariant = (() => {
    if (transaction.status === 'Overdue') return 'destructive' as const;
    if (transaction.status === 'Settled') return 'success' as const;
    if (transaction.status === 'Outstanding') return 'warning' as const;
    if (transaction.status === 'Unknown') return 'secondary' as const;
    return 'info' as const;
  })();

  const isOverdue = transaction.status === 'Overdue';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Transaction Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">

          {/* Amount hero */}
          <div
            className={`rounded-lg p-5 border flex items-center justify-between ${
              isIncome
                ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800'
                : 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
            }`}
          >
            <div className="flex items-center gap-3">
              {isIncome ? (
                <ArrowDownCircle className="h-8 w-8 text-success-600 shrink-0" />
              ) : (
                <ArrowUpCircle className="h-8 w-8 text-destructive shrink-0" />
              )}
              <div>
                <div className="text-sm font-semibold text-foreground mb-0.5">
                  {isIncome ? getCashMovementTerms().in : getCashMovementTerms().out}
                </div>
                <div
                  className={`text-2xl font-bold ${
                    isIncome ? 'text-success-600' : 'text-destructive'
                  }`}
                >
                  {isIncome ? '+' : '-'}
                  {formatCurrency(displayAmount, 'full')}
                </div>
              </div>
            </div>
            <Badge variant={statusVariant} className="text-sm">
              {isOverdue ? 'Overdue' : transaction.status}
            </Badge>
          </div>

          {/* Description */}
          <div>
            <div className="text-sm font-semibold text-foreground mb-1">Description</div>
            <p className="text-sm text-muted-foreground leading-relaxed">{transaction.description}</p>
          </div>

          {/* Date grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            {(transaction.issueDate || transaction.billDate) && (
              <div>
                <div className="text-sm font-semibold text-foreground mb-1">
                  {transaction.issueDate ? 'Issue Date' : 'Bill Date'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDate(transaction.issueDate ?? transaction.billDate!)}
                </div>
              </div>
            )}

            {transaction.dueDate && (
              <div>
                <div className="text-sm font-semibold text-foreground mb-1">Due Date</div>
                <div className={`text-sm ${isOverdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {formatDate(transaction.dueDate)}
                  {isOverdue && <span className="ml-1">(Overdue)</span>}
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-semibold text-foreground mb-1">
                {transaction.status === 'Settled' ? 'Payment Date' : 'Expected Date'}
              </div>
              <div className="text-sm text-muted-foreground">{formatDate(transaction.date)}</div>
            </div>

            {/* Category */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <div className="text-sm font-semibold text-foreground">Category</div>
                {canEdit && !isEditing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-muted-foreground hover:text-foreground"
                    onClick={() => setIsEditing(true)}
                  >
                    <SquarePen className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {isEditing ? (
                <Select
                  value={editedTransaction.category as string}
                  onValueChange={(value) =>
                    setEditedTransaction({ ...editedTransaction, category: value as any })
                  }
                  disabled={categoriesLoading}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder={categoriesLoading ? 'Loading…' : 'Select category'} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.name}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {transaction.category as string}
                </span>
              )}
            </div>

            {/* Confidence if present */}
            {transaction.confidence && (
              <div>
                <div className="text-sm font-semibold text-foreground mb-1">Confidence</div>
                <Badge
                  variant={
                    transaction.confidence === 'certain'
                      ? 'default'
                      : transaction.confidence === 'probable'
                      ? 'secondary'
                      : 'outline'
                  }
                  className="font-normal"
                >
                  {transaction.confidence}
                </Badge>
              </div>
            )}
          </div>

          <Separator />

          {/* Source provenance */}
          {transaction.dataSourceName && (
            <>
              <div>
                <div className="text-sm font-semibold text-foreground mb-2">Source</div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-300 text-xs font-medium">
                    <Database className="w-3 h-3" />
                    {transaction.dataSourceName}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {transaction.dataSourceName.startsWith('Manual Entry')
                      ? 'Manually entered — not from a connected data source'
                      : 'Imported from connected data source'}
                  </span>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Footer metadata */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-foreground">Transaction ID</span>
              <span className="text-sm text-muted-foreground">{transaction.id}</span>
            </div>

            {transaction.reference && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-foreground">Reference</span>
                <span className="text-sm text-muted-foreground">{transaction.reference}</span>
              </div>
            )}
          </div>

        </div>

        {isEditing && (
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-3.5 h-3.5 mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={editedTransaction?.category === transaction?.category}>
              <Save className="w-3.5 h-3.5 mr-1" />
              Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
